import type { ThemeOptions } from '@mui/material';

export const components: ThemeOptions['components'] = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        margin: 0,
        padding: 0,
        scrollbarColor: '#2a2a45 #12121f',
        '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
          background: '#12121f',
        },
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
          background: '#2a2a45',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
          background: '#3d7aff',
        },
      },
    },
  },
  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: { textTransform: 'none', borderRadius: 2, fontWeight: 600 },
      sizeLarge: { padding: '10px 24px', fontSize: '1rem' },
      contained: {
        background: 'linear-gradient(135deg, #1a4cd8 0%, #0033a0 100%)',
        boxShadow: '0 4px 15px rgba(26, 76, 216, 0.3)',
        '&:hover': {
          background: 'linear-gradient(135deg, #3d7aff 0%, #1a4cd8 100%)',
          boxShadow: '0 8px 25px rgba(26, 76, 216, 0.4)',
          transform: 'translateY(-1px)',
        },
      },
      outlined: {
        borderColor: '#1a4cd8',
        '&:hover': {
          background: 'rgba(26, 76, 216, 0.1)',
          borderColor: '#3d7aff',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 2,
        border: '1px solid #2a2a45',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)',
        backgroundImage: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: '#1a4cd8',
          boxShadow: '0 8px 30px rgba(26, 76, 216, 0.15)',
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        backgroundColor: '#12121f',
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        backgroundColor: '#12121f',
      },
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: {
        backgroundColor: '#12121f',
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: {
        '& .MuiTableCell-head': {
          background: 'linear-gradient(180deg, #12121f 0%, #0c0c1a 100%)',
          borderBottom: '2px solid #2a2a45',
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#a0a5b5',
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:last-child td': { borderBottom: 0 },
        '&:hover': {
          background: 'rgba(26, 76, 216, 0.05)',
        },
        transition: 'background 0.2s ease',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        borderColor: '#2a2a45',
        fontSize: '0.875rem',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 2,
        fontWeight: 600,
        fontSize: '0.75rem',
        letterSpacing: '0.5px',
      },
    },
  },
  MuiTextField: {
    defaultProps: { variant: 'outlined', size: 'small' },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          '& fieldset': { borderColor: '#2a2a45' },
          '&:hover fieldset': { borderColor: '#3d7aff' },
          '&.Mui-focused fieldset': { borderColor: '#1a4cd8' },
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 2,
        border: '1px solid #2a2a45',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: '1px solid #2a2a45',
        backgroundImage: 'none',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        borderBottom: '1px solid #2a2a45',
        boxShadow: 'none',
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        borderRadius: 2,
        margin: '2px 8px',
        transition: 'all 0.2s ease',
        '&:hover': {
          background: 'rgba(26, 76, 216, 0.15)',
        },
        '&.Mui-selected': {
          background: 'linear-gradient(135deg, rgba(26, 76, 216, 0.2) 0%, rgba(26, 76, 216, 0.1) 100%)',
          borderLeft: '3px solid #3d7aff',
          '&:hover': {
            background: 'rgba(26, 76, 216, 0.25)',
          },
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        transition: 'all 0.2s ease',
        '&:hover': {
          background: 'rgba(26, 76, 216, 0.15)',
          color: '#3d7aff',
        },
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        background: '#12121f',
        border: '1px solid #2a2a45',
        fontSize: '0.75rem',
        padding: '8px 12px',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 2,
      },
      standardError: {
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
      },
      standardSuccess: {
        background: 'rgba(34, 197, 94, 0.1)',
        border: '1px solid rgba(34, 197, 94, 0.3)',
      },
      standardWarning: {
        background: 'rgba(245, 158, 11, 0.1)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
      },
      standardInfo: {
        background: 'rgba(61, 122, 255, 0.1)',
        border: '1px solid rgba(61, 122, 255, 0.3)',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 600,
        minWidth: 'auto',
        padding: '12px 16px',
        '&.Mui-selected': {
          color: '#3d7aff',
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 3,
        borderRadius: 1,
        background: 'linear-gradient(90deg, #1a4cd8, #3d7aff)',
      },
    },
  },
  MuiBadge: {
    styleOverrides: {
      badge: {
        fontWeight: 600,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 2,
        height: 6,
        background: '#2a2a45',
      },
      bar: {
        borderRadius: 2,
        background: 'linear-gradient(90deg, #1a4cd8, #3d7aff)',
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        color: '#60a5fa',
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
};